import { shortUserFromDto } from '@src/features/users/lib/user';

import type {
  ContactColleague,
  ContactColleagueDto,
  ContactCreateDto,
  ContactCreatePayload,
  ContactDetail,
  ContactDetailDto,
  ContactListItem,
  ContactListItemDto,
  ContactMatch,
  ContactMatchDto,
  ContactPosition,
  ContactPositionDto,
} from '../types';
import { clientAddressFromDto } from './address';

export function contactListItemFromDto(
  contact: ContactListItemDto,
): ContactListItem {
  return {
    id: contact.id,
    status: contact.status,
    name: contact.name,
    portrait: contact.portrait,
    job: contact.job,
    clients: contact.clients,
    poc: shortUserFromDto(contact.poc),
    email: contact.email,
    phone: contact.phone,
  };
}

export function contactMatchFromDto(contact: ContactMatchDto): ContactMatch {
  return {
    ...contactListItemFromDto(contact),
    titleId: contact.title,
    academicTitle: contact.academic_title,
    familyName: contact.family_name,
    firstName: contact.first_name,
    chosenName: contact.chosen_name,
    languageId: contact.language,
    userContact2: contact.user_contact2
      ? shortUserFromDto(contact.user_contact2)
      : null,
    canView: contact.can_view ?? true,
  };
}

/** The person fields of a contact form, as filled from a picked match. */
export type ContactMatchPersonFields = {
  familyName: string;
  firstName: string;
  chosenName: string;
  titleId: string;
  academicTitle: string;
};

/**
 * The person fields a contact form takes from a picked existing contact.
 * Selects hold ids as text, as the select fields do.
 */
export function contactMatchPersonFields(
  contact: ContactMatch,
): ContactMatchPersonFields {
  return {
    familyName: contact.familyName,
    firstName: contact.firstName,
    chosenName: contact.chosenName ?? '',
    titleId: String(contact.titleId),
    academicTitle: contact.academicTitle ?? '',
  };
}

export function contactCreatePayloadToDto(
  payload: ContactCreatePayload,
): ContactCreateDto {
  return {
    existing_contact: payload.existingContactId,
    previous_end: payload.previousEnd,
    title: payload.titleId,
    academic_title: payload.academicTitle,
    family_name: payload.familyName,
    first_name: payload.firstName,
    chosen_name: payload.chosenName,
    user_contact: payload.userContactId,
    user_contact2: payload.userContact2Id,
    client: payload.clientId,
    function_title: payload.functionTitle,
    role: payload.role,
    office_address_usage: payload.officeAddressUsageId,
    email: payload.email,
    mobile: payload.mobile,
    landline: payload.landline,
    manager: payload.managerId,
    start: payload.start,
    email_personal: payload.emailPersonal,
    mobile_personal: payload.mobilePersonal,
    birthdate: payload.birthdate,
    comments: payload.comments,
  };
}

export function contactPositionFromDto(
  position: ContactPositionDto,
): ContactPosition {
  return {
    id: position.id,
    client: position.client,
    functionTitle: position.function_title,
    role: position.role,
    officeAddress: {
      id: position.office_address.id,
      address: clientAddressFromDto(position.office_address.address),
    },
    email: position.email,
    mobile: position.mobile,
    landline: position.landline,
    manager: position.manager,
    rights: position.rights,
    start: position.start,
    end: position.end,
    canEdit: position.can_edit,
  };
}

export function contactColleagueFromDto(
  colleague: ContactColleagueDto,
): ContactColleague {
  return {
    id: colleague.id,
    contact: colleague.contact,
    employer: colleague.employer,
    functionTitle: colleague.function_title,
    role: colleague.role,
    start: colleague.start,
    end: colleague.end,
    kellerContact: shortUserFromDto(colleague.keller_contact),
  };
}

export function contactDetailFromDto(contact: ContactDetailDto): ContactDetail {
  return {
    ...contactMatchFromDto(contact),
    version: contact.version,
    verificationDate: contact.verification_date,
    verifiedBy: shortUserFromDto(contact.verified_by),
    birthdate: contact.birthdate,
    emailPersonal: contact.email_personal,
    mobilePersonal: contact.mobile_personal,
    comments: contact.comments,
    currentPosition: contact.current_position
      ? contactPositionFromDto(contact.current_position)
      : null,
    otherCurrentPositions: contact.other_current_positions.map(
      contactPositionFromDto,
    ),
    formerPositions: contact.former_positions.map(contactPositionFromDto),
    currentColleagues: contact.current_colleagues.map(contactColleagueFromDto),
    formerColleagues: contact.former_colleagues.map(contactColleagueFromDto),
    canEdit: contact.can_edit,
  };
}
